import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const toMoney = (v) => `$${Number(v || 0).toLocaleString()}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
const sanitize = (s) =>
  String(s ?? "")
    .replace(/[–—]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'");

type ExportProjectPdfInput = {
  projectId?: string | number;
  projectName?: string;
  project_type?: string;
  clientName?: string;
  status?: string;
  createdAt?: string;
  dueDate?: string;
  budgetTotals?: {
    planned?: number;
    actual?: number;
    remaining?: number;
  };
  payments?: Array<Record<string, any>>;
};

export async function exportAgentProjectPDF({
  projectId,
  projectName,
  project_type,
  clientName,
  status,
  createdAt,
  dueDate,
  budgetTotals = {},
  payments = [],
}: ExportProjectPdfInput) {
  const doc = new jsPDF({ unit: "pt", format: "a4" }); // A4 page
  const margin = 40;
  let y = margin;

  // Title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.text("Relocation Budget Statement (Agent)", margin, y);
  y += 22;

  // Meta info
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, y);
  y += 16;

  if (clientName) {
    doc.text(`Client: ${sanitize(clientName)}`, margin, y);
    y += 16;
  }
  if (projectName) {
    doc.text(`Project: ${sanitize(projectName)}`, margin, y);
    y += 16;
  }
  if (status) {
    doc.text(`Status: ${sanitize(status)}`, margin, y);
    y += 16;
  }
  if (createdAt) {
    doc.text(`Start Date: ${createdAt}`, margin, y);
    y += 16;
  }
  if (dueDate) {
    doc.text(`End Date: ${dueDate}`, margin, y);
    y += 18;
  }

  // Budget summary table
  autoTable(doc, {
    startY: y,
    head: [["Total Budget (AUD)", "Amount Paid (AUD)", "Remaining (AUD)"]],
    body: [
      [
        toMoney(budgetTotals.planned ?? 0),
        toMoney(budgetTotals.actual ?? 0),
        toMoney(
          budgetTotals.planned != null && budgetTotals.actual != null
            ? budgetTotals.planned - budgetTotals.actual
            : (budgetTotals.remaining ?? 0),
        ),
      ],
    ],
    styles: { font: "helvetica", fontSize: 10, textColor: [0, 0, 0] },
    headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0] },
    margin: { left: margin, right: margin },
    tableWidth: "auto",
  });
  y = doc.lastAutoTable.finalY + 20;

  // Outstanding payments
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Outstanding Payments", margin, y);
  y += 10;
  const outstanding = payments.filter(
    (p) => String(p.payment_status).toLowerCase() === "outstanding",
  );
  autoTable(doc, {
    startY: y,
    head: [["Item", "Amount (AUD)", "Due Date", "Status"]],
    body: outstanding.length
      ? outstanding.map((p) => [
          sanitize(p.item || ""),
          toMoney(p.cost),
          fmtDate(p.due_date),
          "Outstanding",
        ])
      : [["No outstanding payments", "", "", ""]],
    styles: { font: "helvetica", fontSize: 10, textColor: [0, 0, 0] },
    headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0] },
    margin: { left: margin, right: margin },
    tableWidth: "auto",
  });
  y = doc.lastAutoTable.finalY + 20;

  // Paid payments
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("Paid Payments", margin, y);
  y += 10;
  const paid = payments.filter(
    (p) => String(p.payment_status).toLowerCase() === "paid",
  );
  autoTable(doc, {
    startY: y,
    head: [["Item", "Amount (AUD)", "Paid On", "Invoice"]],
    body: paid.length
      ? paid.map((p) => [
          sanitize(p.item || ""),
          toMoney(p.cost),
          fmtDate(p.paid_on),
          sanitize(p.invoice || ""),
        ])
      : [["No paid payments", "", "", ""]],
    styles: { font: "helvetica", fontSize: 10, textColor: [0, 0, 0] },
    headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0] },
    margin: { left: margin, right: margin },
    tableWidth: "auto",
  });

  doc.save(
    `${String(projectName || project_type || "project")
      .replace(/[^\w-]+/g, "_")
      .toLowerCase()}_agent_budget_statement.pdf`,
  );
}
